import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { GoogleLogo, ArrowRight } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";
import { Disclaimer } from "@/components/Disclaimer";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const IMG =
  "https://images.unsplash.com/photo-1746563947316-6f4cc65a66fa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Login() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-slate-950 lg:grid-cols-2">
      <div className="relative hidden lg:block grain-overlay">
        <img src={IMG} alt="" className="h-full w-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-slate-950/20" />
        <div className="absolute bottom-12 left-12 right-12">
          <p className="font-heading text-3xl font-light leading-snug text-slate-100">
            Welcome back to the work of <span className="text-teal-400">knowing yourself</span>.
          </p>
        </div>
      </div>

      <div className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-20">
        <div className="mx-auto w-full max-w-sm">
          <Logo />
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="mt-10 font-heading text-3xl font-light tracking-tight text-slate-50">Log in</h1>
            <p className="mt-2 text-sm text-slate-500">Continue building your living profile.</p>

            <button
              onClick={handleGoogle}
              className="mt-8 flex w-full items-center justify-center gap-3 rounded-lg border border-slate-700 bg-slate-900 px-4 py-3 text-sm font-medium text-slate-200 transition-all hover:border-slate-500"
              data-testid="google-login-btn"
            >
              <GoogleLogo size={18} weight="bold" /> Continue with Google
            </button>

            <div className="my-6 flex items-center gap-3 text-xs text-slate-600">
              <span className="h-px flex-1 bg-slate-800" /> or <span className="h-px flex-1 bg-slate-800" />
            </div>

            <form onSubmit={submit} className="space-y-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                required
                className="w-full rounded-lg border border-slate-800 bg-slate-950 px-4 py-3 text-slate-200 placeholder-slate-600 outline-none transition-all focus:border-teal-500 focus:ring-2 focus:ring-teal-500/40"
                data-testid="login-email-input"
              />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                className="w-full rounded-lg border border-slate-800 bg-slate-950 px-4 py-3 text-slate-200 placeholder-slate-600 outline-none transition-all focus:border-teal-500 focus:ring-2 focus:ring-teal-500/40"
                data-testid="login-password-input"
              />
              {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-teal-500 px-6 py-3 text-sm font-semibold text-slate-950 transition-all hover:bg-teal-400 disabled:opacity-50"
                data-testid="login-submit-btn"
              >
                {loading ? "Logging in…" : "Log in"} {!loading && <ArrowRight size={16} weight="bold" />}
              </button>
            </form>

            <p className="mt-6 text-sm text-slate-500">
              New here?{" "}
              <Link to="/register" className="font-medium text-teal-400 hover:text-teal-300">Create an account</Link>
            </p>
            <div className="mt-8">
              <Disclaimer compact />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
